<template>
  <!-- Embedding hosts (the dashboard window, which has no #dragbar to
       teleport to, or a canvas calendar widget inside a main-window layout)
       pass `embedded` to lay the bar out inline at the top of the calendar,
       without the window's back/forward navigation arrows — teleporting
       there would collide with the host's own top bar. -->
  <BaseTopBar
    v-if="isMounted"
    :embedded="embedded"
    :is-navigation-displayed="!isLeftPaneShown && !embedded"
    :left-panel-width="leftPanelWidth"
  >
    <template #left>
      <div class="calendar-header" :class="{ isLeftPaneShown }">
        <BaseToggleLeftPaneButton
          v-if="!isLeftPaneShown && isLeftPaneAvailable"
        />
        <div
          v-if="showDateNavigation"
          ref="dateEl"
          class="date"
          :class="{ isSelected: isPickerShown }"
          @mouseenter="dateTooltip.showTooltip"
          @mouseleave="dateTooltip.hideTooltip"
          @click="openDatePicker"
        >
          <div class="month">
            {{ headerDate?.month }}
          </div>
          <div class="year">
            {{ headerDate?.year }}
          </div>
        </div>
        <div v-if="showDateNavigation" class="nav">
          <BaseButton
            kind="auxiliary"
            :tooltip="{
              text: navigationActions.previousPeriod.text,
              shortcut: navigationActions.previousPeriod.shortcutsDisplay,
            }"
            tooltip-display="always"
            class="arrow"
            @click="$emit('prevPeriod')"
          >
            <template #icon>
              <ChevronLeftIcon />
            </template>
          </BaseButton>
          <BaseButton
            kind="auxiliary"
            tooltip-display="always"
            :tooltip="{
              text: navigationActions.nextPeriod.text,
              shortcut: navigationActions.nextPeriod.shortcutsDisplay,
            }"
            class="arrow"
            @click="$emit('nextPeriod')"
          >
            <template #icon>
              <ChevronRightIcon />
            </template>
          </BaseButton>
          <BaseButton
            v-if="!isCurrentPeriod"
            kind="auxiliary"
            class="back"
            tooltip-display="always"
            :tooltip="{
              text: 'Current period',
              shortcut: navigationActions.goToToday.shortcutsDisplay,
            }"
            @click="$emit('goToday')"
          >
            <template #icon>
              <BackIcon />
            </template>
          </BaseButton>
        </div>
      </div>
    </template>
    <template #right>
      <div class="right-controls">
        <div v-if="isWeekView && showZoomButtons" class="zoom-controls">
          <BaseButton
            kind="auxiliary"
            :state="!canZoomOut ? 'disabled' : 'default'"
            tooltip-display="always"
            tooltip="Zoom out"
            class="zoom-button"
            @click="$emit('zoomOut')"
          >
            <template #icon>
              <MinusIcon />
            </template>
          </BaseButton>
          <BaseButton
            kind="auxiliary"
            :state="!canZoomIn ? 'disabled' : 'default'"
            tooltip-display="always"
            tooltip="Zoom in"
            class="zoom-button"
            @click="$emit('zoomIn')"
          >
            <template #icon>
              <PlusIcon />
            </template>
          </BaseButton>
        </div>
        <BaseButtonGroup v-if="showLayoutSelector && visibleLayouts.length">
          <template #middleButtons>
            <div
              v-for="(shortcutLayout, shortcutIndex) in layoutShortcuts"
              :key="shortcutIndex"
              class="layout-shortcut"
            >
              <UpgradeButton
                entitlement-key="calendar-layouts"
                :entitled="isEntitledToCalendarLayouts"
                class="layout-shortcut-button"
                kind="main"
                type="secondary"
                :text="layoutLabel(shortcutLayout)"
                :state="activeLayout === shortcutLayout ? 'active' : 'default'"
                @click="$emit('selectLayout', shortcutLayout)"
              />
              <ButtonMenu
                class="layout-shortcut-menu"
                placement="bottom-end"
                :offset="[0, 4]"
                :tabindex="-1"
                :width="132"
              >
                <template #btn="{ isShown }">
                  <BaseButton
                    :class="{ isShown }"
                    class="layout-shortcut-menu-button"
                    kind="main"
                    type="secondary"
                    :accessibility-label="`Customize ${layoutLabel(
                      shortcutLayout
                    )} layout shortcut`"
                  >
                    <template #icon>
                      <ChevronDownIcon />
                    </template>
                  </BaseButton>
                </template>
                <template #content>
                  <template v-for="layout in visibleLayouts" :key="layout">
                    <UpgradeButton
                      entitlement-key="calendar-layouts"
                      :entitled="isEntitledToCalendarLayouts"
                      kind="auxiliary"
                      :text="layoutLabel(layout)"
                      :state="
                        shortcutLayout === layout ? 'disabled' : 'default'
                      "
                      respect-disabled
                      is-expanded
                      @click="updateLayoutShortcut(shortcutIndex, layout)"
                    />
                  </template>
                </template>
              </ButtonMenu>
            </div>
          </template>
        </BaseButtonGroup>
      </div>
    </template>
  </BaseTopBar>
</template>
<script lang="ts">
import {
  computed,
  defineComponent,
  onBeforeUnmount,
  onMounted,
  PropType,
  ref,
  toRefs,
  inject as vueInject,
} from "vue";

import controller from "@ctrl/controller";

import { navigationActions } from "@/components/actions/actions";
import BaseButton from "@/components/base/BaseButton.vue";
import BaseButtonGroup from "@/components/base/BaseButtonGroup.vue";
import BaseToggleLeftPaneButton from "@/components/base/BaseToggleLeftPaneButton.vue";
import BaseTopBar from "@/components/base/BaseTopBar.vue";
import UpgradeButton from "@/components/base/UpgradeButton.vue";
import { getMonth } from "@/components/dates";
import ButtonMenu from "@/components/elements/ButtonMenu.vue";
import BackIcon from "@/components/icons/BackIcon.vue";
import ChevronDownIcon from "@/components/icons/ChevronDownIcon.vue";
import ChevronLeftIcon from "@/components/icons/ChevronLeftIcon.vue";
import ChevronRightIcon from "@/components/icons/ChevronRightIcon.vue";
import MinusIcon from "@/components/icons/MinusIcon.vue";
import PlusIcon from "@/components/icons/PlusIcon.vue";
import * as inject from "@/components/inject";
import {
  DEFAULT_LAYOUT_SHORTCUTS,
  DEFAULT_SELECTABLE_LAYOUTS,
  keyUseLeftPane,
  layoutLabel,
  PLANNER_LAYOUTS,
  PlannerLayout,
  PlannerLayoutShortcuts,
  PlannerSelectableLayouts,
  viewStateToLayout,
} from "@/components/planner/controllers/planner";
import { useTooltip } from "@/components/tooltip";
import { RouteNames } from "@/routes/types";
import { navigateToPlan } from "@/utils/navigate";

export default defineComponent({
  components: {
    BackIcon,
    BaseButton,
    BaseButtonGroup,
    BaseToggleLeftPaneButton,
    BaseTopBar,
    ButtonMenu,
    ChevronDownIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
    MinusIcon,
    PlusIcon,
    UpgradeButton,
  },
  props: {
    calendarDate: { type: Date, required: true },
    canZoomIn: { type: Boolean, default: true },
    canZoomOut: { type: Boolean, default: true },
    /** Lay the bar out inline instead of teleporting it to the window's
     * dragbar — for planners embedded inside another screen's layout. */
    embedded: { type: Boolean, default: false },
    isCurrentPeriod: { type: Boolean, required: true },
    isEntitledToCalendarLayouts: { type: Boolean, required: true },
    isLeftPaneAvailable: { type: Boolean, default: true },
    isThreeDayView: { type: Boolean, required: true },
    customDays: { type: Number, default: 3 },
    isWeekendShown: { type: Boolean, required: true },
    isWeekView: { type: Boolean, required: true },
    leftPanelWidth: { type: Number, default: 0 },
    /**
     * The date navigation cluster: the month/year display (which opens the
     * date picker) and the prev/next/today arrows. Hosts whose own picker
     * drives the calendar date (e.g. a canvas calendar widget) hide it.
     */
    showDateNavigation: { type: Boolean, default: true },
    showLayoutSelector: { type: Boolean, default: true },
    selectableLayouts: {
      type: Object as PropType<PlannerSelectableLayouts>,
      default: () => ({ ...DEFAULT_SELECTABLE_LAYOUTS }),
    },
    layoutShortcuts: {
      type: Array as PropType<PlannerLayoutShortcuts>,
      default: () => [...DEFAULT_LAYOUT_SHORTCUTS],
    },
    showZoomButtons: { type: Boolean, default: true },
  },
  emits: {
    goToDate: (_: Date) => true,
    goToday: () => true,
    nextPeriod: () => true,
    prevPeriod: () => true,
    selectLayout: (_: PlannerLayout) => true,
    updateLayoutShortcut: (_index: number, _layout: PlannerLayout) => true,
    zoomIn: () => true,
    zoomOut: () => true,
  },
  async setup(props, { emit }) {
    const {
      calendarDate,
      selectableLayouts,
      isWeekView,
      isThreeDayView,
      isWeekendShown,
      customDays,
    } = toRefs(props);
    const router = inject.router();
    const profile = inject.profile();
    const datePicker = inject.datePicker();
    const useLeftPane = vueInject(keyUseLeftPane);
    const isLeftPaneShown = computed(() => useLeftPane?.isLeftPaneShown.value);
    const dateEl = ref<HTMLElement>();
    const isPickerShown = ref(false);
    const dateTooltipContent = computed(() => ({
      text: "Go to date",
      shortcut: undefined,
    }));
    const dateTooltip = useTooltip(dateEl, dateTooltipContent, "top");
    onBeforeUnmount(dateTooltip.release);
    // Mandatory due to BaseTopBar teleportation
    const isMounted = ref(false);
    onMounted(async () => {
      isMounted.value = true;
    });
    const headerDate = computed<{ month: string; year: string }>(() => {
      const date = calendarDate.value;
      const month = getMonth(date).slice(0, 3);
      const year = date!.getFullYear().toString();
      return { month, year };
    });
    const advertiseBilling = () => {
      navigateToPlan(router, profile.value.user);
    };

    const visibleLayouts = computed(() =>
      PLANNER_LAYOUTS.filter((layout) => selectableLayouts.value[layout])
    );
    const activeLayout = computed(() =>
      viewStateToLayout({
        isWeekView: isWeekView.value,
        isThreeDayView: isThreeDayView.value,
        isWeekendShown: isWeekendShown.value,
        customDays: customDays.value,
      })
    );
    const updateLayoutShortcut = (index: number, layout: PlannerLayout) => {
      emit("updateLayoutShortcut", index, layout);
      emit("selectLayout", layout);
    };

    // The date picker opens in the mode matching the current layout: a week
    // layout picks a week, the month layout picks a month, and every other
    // (day/rolling) layout picks a single day.
    const pickerMode = computed<"date" | "week" | "month">(() => {
      if (activeLayout.value === "month") return "month";
      if (activeLayout.value === "week") return "week";
      return "date";
    });

    const openDatePicker = () => {
      // Toggle: the picker's own click-outside handler ignores clicks on the
      // trigger, so a re-click lands here and closes it (via `onClose`).
      if (isPickerShown.value) {
        datePicker.value.closePicker();
        return;
      }
      isPickerShown.value = true;
      const mode = pickerMode.value;
      void datePicker.value.showPicker(
        dateEl.value,
        calendarDate.value,
        (selection) => {
          if (!selection) return;
          if (selection.mode === "week") {
            const [start] = controller.datesOfWeek(selection.week);
            emit("goToDate", start);
          } else {
            emit("goToDate", selection.date);
          }
        },
        "left",
        {
          modes: [mode],
          initialMode: mode,
          // Navigation picker: there is nothing to clear.
          showClear: false,
          onClose: () => (isPickerShown.value = false),
        }
      );
    };

    return {
      advertiseBilling,
      router,
      RouteNames,
      getMonth,
      headerDate,
      isLeftPaneShown,
      isMounted,
      navigationActions,
      dateEl,
      dateTooltip,
      isPickerShown,
      openDatePicker,
      updateLayoutShortcut,
      visibleLayouts,
      activeLayout,
      layoutLabel,
    };
  },
});
</script>
<style lang="scss">
.calendar-header {
  display: flex;
  flex-direction: row;
  justify-items: center;
  align-items: center;
  column-gap: 12px;
  &:not(.isLeftPaneShown) {
    margin-inline-start: 12px;
  }
  .date {
    position: relative;
    display: flex;
    width: 80px;
    justify-content: space-between;
    font-size: 18px;
    flex-direction: row;
    border-radius: 5px;
    padding: 2.5px 7px;
    cursor: default;
    &.isSelected,
    &:hover {
      @include themed() {
        background-color: t($grey-05);
      }
    }
    .month {
      font-weight: 600;
      margin-inline-end: 6px;
      text-transform: capitalize;
    }
    .year {
      font-weight: 300;
    }
  }
  .nav {
    display: flex;
    flex-direction: row;
    align-items: center;

    .arrow.base-button.none.auxiliary.default {
      :deep(.left) svg .fill {
        @include themed() {
          fill: t($grey-50);
        }
      }
    }
    .back.base-button.none.auxiliary.default {
      :deep(.left) svg {
        margin-top: -3px;
        .fill {
          @include themed() {
            fill: t($grey-50);
          }
        }
      }
    }
  }
}
.right-controls {
  display: flex;
  flex-direction: row;
  align-items: center;
  column-gap: 12px;
}
.layout-shortcut {
  display: flex;
  flex-direction: row;
  align-items: stretch;

  &:hover {
    button.layout-shortcut-menu-button {
      opacity: 1;
    }
  }

  .layout-shortcut-menu {
    display: flex;
    margin-inline-start: -2px;
  }

  button.base-button.main.layout-shortcut-button {
    padding-inline-end: 12px;
  }

  button.base-button.main.icon-only.layout-shortcut-menu-button {
    padding: 9.5px 5px;
  }

  button.layout-shortcut-menu-button {
    opacity: 0;
    @include transition(opacity 100ms ease-out);

    &.isShown {
      opacity: 1;
    }
  }
}
.zoom-controls {
  display: flex;
  flex-direction: row;
  align-items: center;
  column-gap: 4px;
  .zoom-button.base-button.none.auxiliary.default {
    :deep(.left) svg .stroke {
      @include themed() {
        stroke: t($grey-50);
      }
    }
  }
}
</style>
